// Phase SA-7 — Per-leg verdict card (brief §05.1).
//
// Collapsed: one row → outcome badge · fixture/market/selection/odds
//                       · verdict badge · confidence (dot tier free,
//                       full score Pro) · expand chevron.
//
// Expanded: above row stays + body with:
//   • One-line verdict summary
//   • 2-3 sentence agent reasoning (verbatim from slip_autopsy_legs
//     .reasoning — brief N06: never generic)
//   • STRIKZA's pick row (was the leg in the pipeline?)
//   • Pattern tag row (if pattern_tags non-empty)
//   • Learning applied row (MODEL_ERROR only)

import { useState } from "react";

import { marketLabel, selectionLabel } from "../../exchange/market-format.ts";
import {
  confidenceToDots,
  patternTagsLine,
  strikzaPickLine,
  verdictLabel,
  verdictSummary,
  verdictTone,
  type SlipAutopsyLegRow,
} from "../../slips/slipAutopsyLegs.ts";

export interface LegCardProps {
  leg: SlipAutopsyLegRow;
  /** Pro tier flag — controls dot-tier vs full-score confidence
   * display + access to the expanded reasoning. */
  pro?: boolean;
  /** Initial expanded state. Defaults to false. */
  defaultExpanded?: boolean;
}

export function LegCard({
  leg,
  pro = false,
  defaultExpanded = false,
}: LegCardProps): JSX.Element {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const dots = confidenceToDots(leg.confidence_score, leg.confidence_tier);
  const oddsLabel = leg.odds != null && Number.isFinite(leg.odds)
    ? `${leg.odds.toFixed(2)}×`
    : "—";

  const reasoningText = leg.reasoning ?? "Reasoning not yet generated.";
  const pickLine = strikzaPickLine(leg);
  const patternLine = patternTagsLine(leg.pattern_tags);
  const tone = verdictTone(leg.verdict);

  // Free users see the dot tier + tier label; Pro users also see
  // the numeric score. Promotion-not-restriction.
  const showFullScore = pro && leg.confidence_score != null;

  return (
    <div
      className="sa-leg-card"
      data-testid={`sa-leg-card-${leg.leg_id}`}
      data-expanded={expanded || undefined}
      data-verdict={leg.verdict ?? undefined}
    >
      <button
        type="button"
        className="sa-leg-card-row"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls={`sa-leg-body-${leg.leg_id}`}
        data-testid={`sa-leg-card-toggle-${leg.leg_id}`}
      >
        <span
          className="sa-outcome-badge"
          data-outcome={leg.outcome ?? "unknown"}
          data-testid={`sa-leg-card-outcome-${leg.leg_id}`}
        >
          {leg.outcome ?? "UNKNOWN"}
        </span>

        <span className="sa-leg-card-fixture">
          <span className="sa-leg-card-fixture-title">{leg.fixture_name}</span>
          <span className="sa-leg-card-fixture-meta">
            {`${marketLabel(leg.market)} · ${selectionLabel(leg.market, leg.selection)} `}
            <span className="sa-leg-card-fixture-meta-odds">{`@ ${oddsLabel}`}</span>
          </span>
        </span>

        {leg.verdict ? (
          <span
            className="sa-verdict-badge"
            data-verdict={leg.verdict}
            data-testid={`sa-leg-card-verdict-${leg.leg_id}`}
          >
            {verdictLabel(leg.verdict)}
          </span>
        ) : (
          <span
            className="sa-verdict-badge"
            data-verdict="VOID"
            style={{ color: "var(--sa-fg-muted)" }}
          >
            Pending
          </span>
        )}

        <span
          className="sa-confidence-dots"
          data-testid={`sa-leg-card-confidence-${leg.leg_id}`}
          aria-label={`Confidence tier ${dots.tier}`}
        >
          {[1, 2, 3].map((i) => (
            <span
              key={i}
              className="sa-confidence-dot"
              data-on={i <= dots.dots || undefined}
              aria-hidden
            />
          ))}
          <span className="sa-confidence-tier-label">{dots.tier}</span>
          {showFullScore ? (
            <span
              className="sa-confidence-score"
              data-testid={`sa-leg-card-score-${leg.leg_id}`}
            >
              {`${Math.round(leg.confidence_score!)}/100`}
            </span>
          ) : null}
        </span>

        <span className="sa-leg-card-expand" aria-hidden>
          ▸
        </span>
      </button>

      {expanded ? (
        <div
          id={`sa-leg-body-${leg.leg_id}`}
          className="sa-leg-card-body"
          data-testid={`sa-leg-card-body-${leg.leg_id}`}
        >
          <div
            className="sa-leg-card-summary"
            data-testid={`sa-leg-card-summary-${leg.leg_id}`}
          >
            {verdictSummary(leg)}
          </div>

          <p
            className="sa-leg-card-reasoning"
            data-testid={`sa-leg-card-reasoning-${leg.leg_id}`}
          >
            {reasoningText}
          </p>

          <div
            className="sa-leg-card-meta-row"
            data-testid={`sa-leg-card-strikza-${leg.leg_id}`}
            data-tone={tone || undefined}
          >
            <span className="sa-leg-card-meta-icon">↳</span>
            <span>{pickLine}</span>
          </div>

          {patternLine ? (
            <div
              className="sa-leg-card-meta-row"
              data-testid={`sa-leg-card-pattern-${leg.leg_id}`}
              data-tone="amber"
            >
              <span className="sa-leg-card-meta-icon">★</span>
              <span>{patternLine}</span>
            </div>
          ) : null}

          {leg.verdict === "MODEL_ERROR" && leg.learning_applied ? (
            <div
              className="sa-leg-card-meta-row"
              data-testid={`sa-leg-card-learning-${leg.leg_id}`}
              data-tone="red"
            >
              <span className="sa-leg-card-meta-icon">●</span>
              <span>{`Learning applied: ${leg.learning_applied}`}</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
